import { useCallback, useEffect, useState } from "react";
import {
  CalendarDays,
  History,
  Loader2,
  Square,
  Trash2,
  FlaskConical,
  CheckCheck,
} from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { cn } from "../../lib/utils";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

// Restaurar as conversas que já estavam no aparelho.
//
// Antes isto só acontecia no instante da conexão: quem esqueceu de marcar
// "importar histórico" antes de ler o QR tinha de desconectar e ler o QR de
// novo — derrubando o atendimento em andamento — para ver o passado.
//
// São duas escolhas, e elas são bem diferentes na prática: descer até o começo
// da conversa pode levar horas e trazer anos de mensagem; parar numa data traz
// só o período que interessa. Por isso a data é a opção que já vem marcada.
//
// A busca roda no servidor: fechar esta janela não interrompe nada, e reabrir
// mostra onde está. Parar é decisão explícita, no botão.
const emDia = (data) => data.toISOString().slice(0, 10);

// Como cada situação de rodada é dita para quem lê.
//
// "Concluída" nunca vira "veio tudo". O WhatsApp entrega o que ainda tem e não
// informa o que deixou de entregar — prometer completude aqui faria a pessoa
// parar de procurar uma conversa que simplesmente não chegou.
const SITUACOES = {
  pending: { rotulo: "Aguardando", tom: "text-muted-foreground" },
  collecting: { rotulo: "Preparando", tom: "text-muted-foreground" },
  processing: { rotulo: "Em andamento", tom: "text-primary" },
  completed: { rotulo: "Concluída", tom: "text-emerald-600" },
  partial: { rotulo: "Parcial", tom: "text-amber-600" },
  cancelled: { rotulo: "Cancelada", tom: "text-muted-foreground" },
  failed: { rotulo: "Falhou", tom: "text-destructive" },
  coverage_unknown: { rotulo: "Alcance desconhecido", tom: "text-amber-600" },
};

const emData = (valor) =>
  valor ? new Date(valor).toLocaleDateString("pt-BR") : "—";

const RestaurarConversasModal = ({ aberto, aoFechar, conexao }) => {
  const [modo, setModo] = useState("data");
  const [data, setData] = useState(() => {
    const trintaDiasAtras = new Date();
    trintaDiasAtras.setDate(trintaDiasAtras.getDate() - 30);
    return emDia(trintaDiasAtras);
  });
  const [estado, setEstado] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [confirmarDesfazer, setConfirmarDesfazer] = useState(false);
  const [desfazendo, setDesfazendo] = useState(false);
  // Hoje: o caso comum é desfazer a importação que acabou de sair errada.
  const [dataDesfazer, setDataDesfazer] = useState(() => emDia(new Date()));

  // Escolhas da rodada. Ficam na tela e não na conexão porque são da RODADA:
  // "desta vez traga também os grupos" não é a mesma coisa que mudar o padrão
  // da conexão para sempre.
  const [incluirGrupos, setIncluirGrupos] = useState(false);
  const [fecharAoFinal, setFecharAoFinal] = useState(false);
  const [situacaoDoTicket, setSituacaoDoTicket] = useState("closed");
  const [filaId, setFilaId] = useState("");
  const [filas, setFilas] = useState([]);

  const [ensaiando, setEnsaiando] = useState(false);
  const [medida, setMedida] = useState(null);
  const [lotes, setLotes] = useState([]);
  const [agindoNoLote, setAgindoNoLote] = useState(null);

  const whatsappId = conexao?.id;

  const consultar = useCallback(() => {
    if (!whatsappId) return Promise.resolve();

    return api
      .get(`/whatsapp/${whatsappId}/restore`)
      .then(({ data: atual }) => setEstado(atual))
      .catch(() => {});
  }, [whatsappId]);

  const carregarLotes = useCallback(() => {
    if (!whatsappId) return Promise.resolve();

    return api
      .get(`/whatsapp/${whatsappId}/import-jobs`)
      .then(({ data: lista }) => setLotes(Array.isArray(lista) ? lista : []))
      .catch(() => {});
  }, [whatsappId]);

  // Total acumulado gravado na conexão. Vem do banco, então sobrevive a um F5
  // e a um reinício do servidor no meio da busca — o contador do socket some
  // junto com a página.
  const gravado = conexao?.importProgress || null;

  // Ao abrir, saber se já existe uma restauração andando desta conexão — foi
  // ela que ficou rodando depois que a janela fechou.
  useEffect(() => {
    if (!aberto) return undefined;

    consultar();
    carregarLotes();

    api
      .get("/queue")
      .then(({ data: lista }) => setFilas(Array.isArray(lista) ? lista : []))
      .catch(() => {});

    const socket = openSocket();

    socket.on("import", (dados) => {
      if (Number(dados?.whatsappId) !== Number(whatsappId)) return;

      setEstado((anterior) => ({
        ...(anterior || {}),
        ...dados,
        emAndamento: dados.action !== "finished",
      }));
    });

    // O lote muda de situação no servidor — inclusive quando quem mandou parar
    // foi outra aba. Sem ouvir isto, a lista de rodadas ficaria mentindo até a
    // próxima abertura da janela.
    socket.on("importJob", () => {
      carregarLotes();
    });

    return () => socket.disconnect();
  }, [aberto, consultar, carregarLotes, whatsappId]);

  const emAndamento = Boolean(estado?.emAndamento);
  // Ficou marcada no banco, mas nenhum laço está rodando: reinício do backend
  // ou queda da conexão no meio da varredura.
  const pendente = !emAndamento && Boolean(estado?.pendente);

  const opcoesDaRodada = () => ({
    ate: modo === "data" ? data : null,
    incluirGrupos,
    fecharAoFinal,
    situacaoDoTicket,
    filaId: filaId ? Number(filaId) : null,
  });

  // O ensaio: mede o alcance sem gravar nada.
  //
  // É a diferença entre descobrir agora que a rodada vai mexer em 900
  // conversas e descobrir daqui a três horas, com elas já criadas.
  const ensaiar = async () => {
    setEnsaiando(true);

    try {
      const { data: r } = await api.post(`/whatsapp/${whatsappId}/restore`, {
        ...opcoesDaRodada(),
        ensaio: true,
      });

      setMedida(r.medida);
      await carregarLotes();
    } catch (err) {
      toastError(err);
    }

    setEnsaiando(false);
  };

  const começar = async () => {
    setEnviando(true);

    try {
      const { data: novo } = await api.post(
        `/whatsapp/${whatsappId}/restore`,
        opcoesDaRodada(),
      );
      setEstado(novo);
      setMedida(null);
      await carregarLotes();
    } catch (err) {
      toastError(err);
    }

    setEnviando(false);
  };

  const parar = async () => {
    try {
      await api.delete(`/whatsapp/${whatsappId}/restore`);
      await consultar();
      await carregarLotes();
    } catch (err) {
      toastError(err);
    }
  };

  // Fecha os atendimentos que ESTA rodada criou — e só eles. Atendimento que a
  // equipe reabriu depois fica de pé.
  const fecharDoLote = async (lote) => {
    setAgindoNoLote(lote.id);

    try {
      const { data: r } = await api.post(
        `/import-jobs/${lote.id}/close-created-tickets`,
      );

      toast.success(
        r.fechados
          ? `${r.fechados} atendimento(s) desta rodada fechado(s).`
          : "Nenhum atendimento aberto desta rodada.",
      );

      await carregarLotes();
    } catch (err) {
      toastError(err);
    }

    setAgindoNoLote(null);
  };

  // Desfaz uma rodada inteira: saem as mensagens que ela gravou e os
  // atendimentos que ela criou. O que já existia antes fica.
  const desfazerLote = async (lote) => {
    setAgindoNoLote(lote.id);

    try {
      const { data: r } = await api.post(`/import-jobs/${lote.id}/undo`);

      toast.success(
        `${r.atendimentos} atendimento(s) e ${r.mensagens} mensagem(ns) desta rodada removidos.`,
      );

      await carregarLotes();
    } catch (err) {
      toastError(err);
    }

    setAgindoNoLote(null);
  };

  // Desfazer por data. É a saída para o que foi importado antes de as rodadas
  // existirem — esse histórico não tem rodada a que pertencer.
  const desfazer = async () => {
    if (!dataDesfazer) return;

    setDesfazendo(true);

    try {
      const { data: r } = await api.post(
        `/whatsapp/${whatsappId}/import/undo`,
        { desde: `${dataDesfazer}T00:00:00` },
      );

      toast.success(
        r.apagados
          ? `${r.apagados} atendimento(s) importado(s) removido(s).`
          : "Nenhum atendimento importado nesse período.",
      );

      setConfirmarDesfazer(false);
      await consultar();
      await carregarLotes();
    } catch (err) {
      toastError(err);
    }

    setDesfazendo(false);
  };

  const opcao = (valor, icone, titulo, descricao) => (
    <button
      type="button"
      onClick={() => setModo(valor)}
      disabled={emAndamento}
      className={cn(
        "flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors",
        modo === valor
          ? "border-primary bg-primary/5"
          : "hover:bg-muted/60 disabled:hover:bg-transparent",
        emAndamento && "opacity-60",
      )}
    >
      <span
        className={cn(
          "mt-0.5 shrink-0 rounded-md p-1.5",
          modo === valor
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-muted-foreground",
        )}
      >
        {icone}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{titulo}</span>
        <span className="block text-xs text-muted-foreground">{descricao}</span>
      </span>
    </button>
  );

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Restaurar conversas</DialogTitle>
          <DialogDescription>
            Busca no aparelho as mensagens anteriores às que o sistema já tem
            {conexao?.name ? ` em ${conexao.name}` : ""}.
          </DialogDescription>
        </DialogHeader>

        {!conexao?.importHistory && (
          // O despejo de todas as conversas de uma vez só existe no momento em
          // que o QR é lido. Sem dizer isso aqui, a pessoa repete a busca
          // achando que ela falhou.
          <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-muted-foreground">
            Esta conexão está com a importação de histórico desligada. A busca
            traz o que o aparelho ainda tiver de cada conversa. Para receber o
            histórico completo de todos os contatos de uma vez, ligue "Importar
            histórico do aparelho" na conexão e leia o QR Code de novo.
          </p>
        )}

        <div className="space-y-3">
          {opcao(
            "data",
            <CalendarDays className="h-4 w-4" />,
            "A partir de uma data",
            "Desce até o dia escolhido e para.",
          )}

          {modo === "data" && (
            <div className="ml-11 space-y-1.5">
              <Label htmlFor="restaurar-data" className="text-xs">
                Trazer conversas até
              </Label>
              <Input
                id="restaurar-data"
                type="date"
                value={data}
                max={emDia(new Date())}
                disabled={emAndamento}
                onChange={(e) => setData(e.target.value)}
              />
            </div>
          )}

          {opcao(
            "tudo",
            <History className="h-4 w-4" />,
            "Tudo",
            "Vai até o começo de cada conversa. Pode levar horas.",
          )}
        </div>

        {/* O que esta rodada faz com o que trouxer. São escolhas da rodada, não
            da conexão: mudar aqui não muda o padrão para as próximas. */}
        <div className="space-y-3 rounded-lg border p-3">
          <p className="text-xs font-medium text-muted-foreground">
            Nesta rodada
          </p>

          <div className="flex items-start justify-between gap-3">
            <Label
              htmlFor="restaurar-grupos"
              className="text-xs font-normal leading-snug"
            >
              Incluir conversas de grupo
              <span className="block text-[11px] text-muted-foreground">
                Grupo importado enche a lista de conversa que ninguém vai
                atender.
              </span>
            </Label>
            <Switch
              id="restaurar-grupos"
              checked={incluirGrupos}
              disabled={emAndamento}
              onCheckedChange={setIncluirGrupos}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="restaurar-situacao" className="text-xs">
              Situação dos atendimentos importados
            </Label>
            <Select
              value={situacaoDoTicket}
              disabled={emAndamento}
              onValueChange={setSituacaoDoTicket}
            >
              <SelectTrigger id="restaurar-situacao">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="closed">Resolvido</SelectItem>
                <SelectItem value="pending">Aguardando</SelectItem>
                <SelectItem value="open">Em atendimento</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="restaurar-fila" className="text-xs">
              Fila dos atendimentos importados
            </Label>
            <Select
              value={filaId || "none"}
              disabled={emAndamento}
              onValueChange={(v) => setFilaId(v === "none" ? "" : v)}
            >
              <SelectTrigger id="restaurar-fila">
                <SelectValue placeholder="Sem fila" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem fila</SelectItem>
                {filas.map((fila) => (
                  <SelectItem key={fila.id} value={String(fila.id)}>
                    {fila.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start justify-between gap-3">
            <Label
              htmlFor="restaurar-fechar"
              className="text-xs font-normal leading-snug"
            >
              Fechar os atendimentos ao terminar
              <span className="block text-[11px] text-muted-foreground">
                Só os que esta rodada criar. Nenhuma mensagem é enviada ao
                cliente.
              </span>
            </Label>
            <Switch
              id="restaurar-fechar"
              checked={fecharAoFinal}
              disabled={emAndamento}
              onCheckedChange={setFecharAoFinal}
            />
          </div>
        </div>

        {/* O resultado do ensaio. Diz o alcance em conversas, nunca em
            mensagens: o WhatsApp não informa o volume antes de mandar. */}
        {medida && (
          <div className="rounded-lg border bg-muted/40 p-3 text-sm">
            <p className="font-medium">Ensaio</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {medida.conversasNaFaixa} de {medida.conversasConhecidas}{" "}
              conversa(s) conhecidas entram nesta faixa
              {medida.maisAntigaConhecida
                ? ` · a mais antiga que o aparelho conhece é de ${emData(
                    medida.maisAntigaConhecida,
                  )}`
                : ""}
              . Já há {medida.mensagensJaNoSistema} mensagem(ns) desta conexão
              no sistema.
            </p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {medida.aviso}
            </p>
          </div>
        )}

        {/* Marcada para retomar, mas sem ninguém tocando agora — o backend foi
            reiniciado, ou a conexão caiu, no meio da varredura. Sem este aviso
            a tela dizia "nada rodando" e o botão recusava começar de novo, sem
            explicar por quê. */}
        {pendente && (
          <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-muted-foreground">
            Havia uma restauração em andamento que ficou pela metade
            {estado?.aguardandoConexao
              ? " e a conexão está fora do ar. Ela recomeça sozinha assim que o número reconectar."
              : ". Ela recomeça sozinha na próxima reconexão."}{" "}
            Use “Cancelar restauração” se não quiser mais.
          </p>
        )}

        {/* Total que já entrou nesta conexão, lido do banco. Fica fora do
            bloco de andamento de propósito: ele aparece mesmo depois de a
            página ter sido recarregada, que é justamente quando o contador
            do socket não existe mais. */}
        {gravado?.mensagens > 0 && !emAndamento && (
          <div className="rounded-lg border bg-muted/40 p-3 text-sm">
            <p className="font-medium">
              {gravado.mensagens} mensagem
              {gravado.mensagens > 1 ? "s" : ""} já importada
              {gravado.mensagens > 1 ? "s" : ""} nesta conexão
            </p>
            {gravado.atualizadoEm && (
              <p className="mt-1 text-xs text-muted-foreground">
                Último lote em{" "}
                {new Date(gravado.atualizadoEm).toLocaleString("pt-BR")}
                {gravado.lotes ? ` · ${gravado.lotes} lotes` : ""}
              </p>
            )}
          </div>
        )}

        {estado && (estado.emAndamento || estado.rodadas > 0) && (
          <div className="rounded-lg border bg-muted/40 p-3 text-sm">
            <div className="flex items-center gap-2 font-medium">
              {emAndamento ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Buscando no aparelho…
                </>
              ) : (
                "Restauração concluída"
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {estado.mensagens || 0} mensagens recuperadas
              {emAndamento && estado.faltam
                ? ` · ${estado.faltam} de ${estado.conversas} conversas ainda buscando`
                : ""}
              {estado.maisAntiga
                ? ` · a conversa já recuou até ${new Date(
                    estado.maisAntiga,
                  ).toLocaleDateString("pt-BR")}`
                : ""}
            </p>
          </div>
        )}

        {/* As rodadas anteriores, com o relatório de cada uma.
            Um total só ("importei 300") não diz se deu certo: 300 de 300 é
            sucesso, 300 de 30 mil com 29.700 "já existiam" é uma reimportação
            inofensiva, e 300 com mil falhas é um problema. */}
        {lotes.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">
              Rodadas desta conexão
            </p>

            {lotes.map((lote) => {
              const situacao = SITUACOES[lote.status] || {
                rotulo: lote.status,
                tom: "text-muted-foreground",
              };

              const ativo = ["pending", "collecting", "processing"].includes(
                lote.status,
              );

              return (
                <div key={lote.id} className="rounded-lg border p-3 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">
                      {new Date(lote.createdAt).toLocaleString("pt-BR")}
                      {lote.dryRun ? " · ensaio" : ""}
                    </span>
                    <span className={cn("font-medium", situacao.tom)}>
                      {situacao.rotulo}
                    </span>
                  </div>

                  <p className="mt-1 text-muted-foreground">
                    {lote.imported} importada(s) · {lote.existing} já existia(m)
                    · {lote.skipped} ignorada(s) · {lote.failed} falha(s)
                  </p>

                  {/* A cobertura observada. É a única resposta honesta para
                      "veio tudo?" — o WhatsApp entrega o que ainda tem. */}
                  {(lote.oldestReceivedAt || lote.newestReceivedAt) && (
                    <p className="mt-0.5 text-muted-foreground">
                      Chegou de {emData(lote.oldestReceivedAt)} a{" "}
                      {emData(lote.newestReceivedAt)}
                      {lote.coverageStatus === "observed"
                        ? " (faixa recebida, não necessariamente tudo o que existe no aparelho)"
                        : ""}
                    </p>
                  )}

                  {lote.sanitizedError && (
                    <p className="mt-0.5 text-destructive">
                      {lote.sanitizedError}
                    </p>
                  )}

                  {!lote.dryRun && !ativo && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1.5 text-xs"
                        disabled={agindoNoLote === lote.id}
                        onClick={() => fecharDoLote(lote)}
                      >
                        <CheckCheck className="h-3.5 w-3.5" />
                        Fechar os desta rodada
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1.5 text-xs text-destructive hover:text-destructive"
                        disabled={agindoNoLote === lote.id}
                        onClick={() => desfazerLote(lote)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Desfazer esta rodada
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Desfazer por data. Só aparece quando há o que desfazer — um botão de
            apagar atendimento não pode ficar à toa numa tela que a pessoa abre
            para trazer conversa. */}
        {gravado?.mensagens > 0 && !emAndamento && (
          <div className="space-y-2 rounded-lg border border-destructive/40 p-3">
            {!confirmarDesfazer ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-full text-xs text-destructive hover:text-destructive"
                onClick={() => setConfirmarDesfazer(true)}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Desfazer importação por data
              </Button>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">
                  Apaga os atendimentos que <strong>nasceram de
                  importação</strong> nesta conexão a partir da data escolhida,
                  com as mensagens deles. Conversa atendida pela equipe não é
                  tocada. Não tem volta. Para desfazer só uma rodada, use o
                  botão dela na lista acima.
                </p>
                <div className="space-y-1.5">
                  <Label htmlFor="desfazer-data" className="text-xs">
                    Apagar o que foi importado a partir de
                  </Label>
                  <Input
                    id="desfazer-data"
                    type="date"
                    value={dataDesfazer}
                    max={emDia(new Date())}
                    onChange={(e) => setDataDesfazer(e.target.value)}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setConfirmarDesfazer(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="h-7 gap-1.5 text-xs"
                    disabled={desfazendo || !dataDesfazer}
                    onClick={desfazer}
                  >
                    {desfazendo && (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    )}
                    Apagar de verdade
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={aoFechar}>
            Fechar
          </Button>

          {!emAndamento && !pendente && (
            <Button
              variant="outline"
              onClick={ensaiar}
              disabled={ensaiando || (modo === "data" && !data)}
              className="gap-1.5"
            >
              {ensaiando ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FlaskConical className="h-3.5 w-3.5" />
              )}
              Ensaiar
            </Button>
          )}

          {emAndamento || pendente ? (
            <Button variant="destructive" onClick={parar} className="gap-1.5">
              <Square className="h-3.5 w-3.5" />
              {emAndamento ? "Parar" : "Cancelar restauração"}
            </Button>
          ) : (
            <Button
              onClick={começar}
              disabled={enviando || (modo === "data" && !data)}
              className="gap-1.5"
            >
              {enviando ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <History className="h-3.5 w-3.5" />
              )}
              Restaurar
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RestaurarConversasModal;
